/* ─── Trigger ─── */

.trigger {
  cursor: pointer;
}

/* ─── Backdrop ─── */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: rgb(var(--md-scrim) / var(--scrim-opacity));
  backdrop-filter: blur(4px);
  opacity: calc(1 - var(--drawer-swipe-progress, 0));
  transition: opacity var(--motion-duration-medium1)
    var(--motion-easing-standard);
}

.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

/* ─── Popup ─── */

.popup {
  position: fixed;
  z-index: var(--z-modal);
  background: var(--md-surface-container-low);
  box-shadow: var(--elevation-3);
  overflow-y: auto;
  padding: var(--space-6);
  outline: none;
  transition: transform var(--motion-duration-medium1)
    var(--motion-easing-standard);
}

/* Base UI writes the finger's offset to --drawer-swipe-movement-x/-y while
   swiping, and scales the release by --drawer-swipe-strength. */
.popup[data-swiping] {
  transition: none;
  user-select: none;
}

.popup[data-ending-style] {
  transition-duration: calc(
    var(--drawer-swipe-strength, 1) * var(--motion-duration-medium1)
  );
}

/* ─── Side: right ─── */

.popup[data-side="right"] {
  inset: 0 0 0 auto;
  transform: translateX(var(--drawer-swipe-movement-x, 0px));
}

.popup[data-side="right"][data-starting-style],
.popup[data-side="right"][data-ending-style] {
  transform: translateX(100%);
}

/* ─── Side: left ─── */

.popup[data-side="left"] {
  inset: 0 auto 0 0;
  transform: translateX(var(--drawer-swipe-movement-x, 0px));
}

.popup[data-side="left"][data-starting-style],
.popup[data-side="left"][data-ending-style] {
  transform: translateX(-100%);
}

/* ─── Side: bottom ─── */

.popup[data-side="bottom"] {
  inset: auto 0 0 0;
  max-height: 90dvh;
  border-radius: var(--shape-xl) var(--shape-xl) 0 0;
  transform: translateY(var(--drawer-swipe-movement-y, 0px));
}

.popup[data-side="bottom"][data-starting-style],
.popup[data-side="bottom"][data-ending-style] {
  transform: translateY(100%);
}

/* ─── Side: top ─── */

.popup[data-side="top"] {
  inset: 0 0 auto 0;
  max-height: 90dvh;
  border-radius: 0 0 var(--shape-xl) var(--shape-xl);
  transform: translateY(var(--drawer-swipe-movement-y, 0px));
}

.popup[data-side="top"][data-starting-style],
.popup[data-side="top"][data-ending-style] {
  transform: translateY(-100%);
}

/* ─── Size variants (horizontal sides) ─── */

.popup[data-side="right"].size-sm,
.popup[data-side="left"].size-sm {
  width: 320px;
}

.popup[data-side="right"].size-md,
.popup[data-side="left"].size-md {
  width: 400px;
}

.popup[data-side="right"].size-lg,
.popup[data-side="left"].size-lg {
  width: 560px;
}

.popup[data-side="right"].size-full,
.popup[data-side="left"].size-full {
  width: 100vw;
}

/* ─── Drag handle (bottom/top sheets) ─── */

.handle {
  width: 32px;
  height: 4px;
  background: var(--md-outline);
  border-radius: var(--shape-full);
  margin: 0 auto var(--space-3);
  flex-shrink: 0;
}

/* ─── Close button ─── */

.close {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  border-radius: var(--shape-full);
  background: transparent;
  color: var(--md-on-surface-variant);
  cursor: pointer;
}

.close::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .close:hover::before {
    opacity: var(--state-hover);
  }
}

.close:focus-visible::before {
  opacity: var(--state-focus);
}

.close:active::before {
  opacity: var(--state-pressed);
}

.close svg {
  width: 1rem;
  height: 1rem;
}

/* ─── Title ─── */

.title {
  font: var(--type-headline-sm);
  letter-spacing: var(--tracking-headline-sm);
  font-family: var(--font-sans);
  color: var(--md-on-surface);
  margin: 0;
}

/* ─── Description ─── */

.description {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  font-family: var(--font-sans);
  color: var(--md-on-surface-variant);
  margin: 0;
}

/* ─── Header ─── */

.header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding-block-end: var(--space-4);
}

/* ─── Body / Actions layout ─── */

.popup:has(.body) {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  padding-block-end: 0;
}

/* Bleed past the popup's inline padding and re-pad, so content aligns with the
   header while the scrollbar and the actions' border reach the panel edge. */
.body,
.actions {
  margin-inline: calc(-1 * var(--space-6));
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-6);
}

.actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  border-block-start: 1px solid var(--md-outline-variant);
}
